import type { FieldValues, Path, UseFormSetError } from "react-hook-form";
import { ApiError } from "../../lib/api/client";
import { toast } from "../../lib/ui/toast";

/**
 * Route a failed form submit to the right place — ONE copy for every form
 * modal:
 *
 *   1. a code the form knows how to explain (409 `details.field`,
 *      USERNAME_TAKEN, …) → that field, with the modal's translated message;
 *   2. a 422 whose `field_errors` name fields this form renders → each of those
 *      fields, with the localized "invalid value" message;
 *   3. anything else → a toast (`toast.error` localizes by `error.code`).
 *
 * Step 2 deliberately does NOT show the server's `field_errors` text: the API
 * speaks English on the wire (CLAUDE.md § i18n), and the client-side zod
 * schema already catches the common cases with translated copy. What reaches
 * here is a rule the client didn't know about — marking the field is the job.
 */
export interface ApiErrorRouting<T extends FieldValues> {
	/** Fields this form renders; errors on anything else fall through to the toast. */
	known: readonly Path<T>[];
	/** Toast prefix: "Couldn't create user". */
	prefix: string;
	/** Localized "Invalid value". */
	invalidValue: string;
	/** Map a coded error to a field + translated message; return null to fall through. */
	fieldFor?: (err: ApiError) => { field: Path<T>; message: string } | null | undefined;
}

export function applyApiError<T extends FieldValues>(
	err: unknown,
	setError: UseFormSetError<T>,
	{ known, prefix, invalidValue, fieldFor }: ApiErrorRouting<T>,
): void {
	if (err instanceof ApiError) {
		const hit = fieldFor?.(err);
		if (hit && known.includes(hit.field)) {
			setError(hit.field, { message: hit.message });
			return;
		}
		const fields = Object.keys(err.fieldErrors).filter((k): k is Path<T> => known.includes(k as Path<T>));
		if (fields.length > 0) {
			for (const k of fields) setError(k, { message: invalidValue });
			return;
		}
	}
	toast.error(err, prefix);
}

/** The field a 409 CONFLICT names in `details.field`, or null. */
export function conflictField(err: ApiError): string | null {
	if (err.code !== "CONFLICT") return null;
	const field = err.details?.["field"];
	return typeof field === "string" ? field : null;
}
